// pages/ErrorLogs.js
import React, { useState, useEffect, useRef } from 'react';
import { getIcon } from '../utils/icons';
import api from '../services/api';
import { toast } from 'react-toastify';

const ErrorLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [severityFilter, setSeverityFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const filtersInitialized = useRef(false);

  const fetchLogs = async (pageNum) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', pageNum);
      params.set('pageSize', '30');
      if (severityFilter) params.set('severity', severityFilter);
      if (dateFilter) {
        params.set('startDate', new Date(dateFilter + 'T00:00:00').toISOString());
        params.set('endDate', new Date(dateFilter + 'T23:59:59').toISOString());
      }

      const response = await api.get(`/logs/errors?${params.toString()}`);
      const data = response.data;
      setLogs(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (error) {
      toast.error('Не удалось загрузить логи ошибок');
      console.error('Error loading error logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page);
  }, [page]);

  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    setPage(1);
    fetchLogs(1);
  }, [severityFilter, dateFilter]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'fatal': return 'bg-red-100 text-red-800';
      case 'error': return 'bg-red-50 text-red-700';
      case 'warning': return 'bg-yellow-50 text-yellow-700';
      case 'info': return 'bg-blue-50 text-blue-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU');
  };

  const parseContext = (contextStr) => {
    if (!contextStr) return null;
    try {
      return typeof contextStr === 'string' ? JSON.parse(contextStr) : contextStr;
    } catch {
      return contextStr;
    }
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Логи ошибок приложения</h1>
        <p className="text-gray-600">Всего записей: {totalCount}</p>
      </header>

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
          <select
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <option value="">Все</option>
            <option value="fatal">Fatal</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Дата</label>
          <input
            type="date"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>
        {(severityFilter || dateFilter) && (
          <button
            onClick={() => { setSeverityFilter(''); setDateFilter(''); }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Сбросить
          </button>
        )}
      </div>

      {/* Таблица */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Ошибок не найдено</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Сообщение</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Пользователь</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Версия</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                  >
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(log.severity)}`}>
                        {log.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-md truncate">
                      {log.errorMessage}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {log.userName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.appVersion || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(log.createdAt)}
                    </td>
                  </tr>
                  {expandedRow === log.id && (
                    <tr>
                      <td colSpan={5} className="px-4 py-4 bg-gray-50">
                        <div className="space-y-3">
                          <div>
                            <span className="text-xs font-medium text-gray-500">Сообщение:</span>
                            <p className="text-sm text-gray-800 mt-1">{log.errorMessage}</p>
                          </div>
                          {log.errorStack && (
                            <div>
                              <span className="text-xs font-medium text-gray-500">Stack trace:</span>
                              <pre className="text-xs text-gray-600 mt-1 bg-gray-100 p-3 rounded overflow-x-auto max-h-48">
                                {log.errorStack}
                              </pre>
                            </div>
                          )}
                          {log.errorContext && (
                            <div>
                              <span className="text-xs font-medium text-gray-500">Контекст:</span>
                              <pre className="text-xs text-gray-600 mt-1 bg-gray-100 p-3 rounded overflow-x-auto">
                                {JSON.stringify(parseContext(log.errorContext), null, 2)}
                              </pre>
                            </div>
                          )}
                          <div className="flex gap-6 text-xs text-gray-500">
                            <span>Device ID: {log.deviceId || '-'}</span>
                            <span>User ID: {log.userId || '-'}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Стр. {page} из {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Назад
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Далее
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorLogs;
